import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";
import { format, parseISO } from "date-fns";

/**
 * Convert a local date and time in a specific timezone to a UTC ISO string for database storage.
 * 
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:mm format (24-hour)
 * @param timezone - IANA timezone string (e.g., "America/Jamaica")
 * @returns UTC ISO string ready for database storage
 * 
 * Example: localToUtc("2026-01-27", "19:00", "America/Jamaica") 
 *          → "2026-01-28T00:00:00.000Z" (7PM Jamaica = midnight UTC next day)
 */
export function localToUtc(date: string, time: string, timezone: string): string {
  // Create a naive datetime string (no timezone info)
  const naiveDatetime = `${date}T${time}:00`;
  
  // fromZonedTime interprets the naive datetime as being in the specified timezone
  // and converts it to UTC
  const utcDate = fromZonedTime(naiveDatetime, timezone);
  
  return utcDate.toISOString();
}

/**
 * Format a UTC timestamp for display in the event's timezone.
 * 
 * @param utcDate - UTC timestamp string from database (ISO format or timestamptz)
 * @param timezone - IANA timezone string for the event
 * @param formatStr - date-fns format string
 * @returns Formatted date string in the event's timezone
 * 
 * Example: formatEventTime("2026-01-28T00:00:00Z", "America/Jamaica", "h:mm a")
 *          → "7:00 PM"
 */
export function formatEventTime(
  utcDate: string,
  timezone: string,
  formatStr: string = "EEEE, MMMM d, yyyy 'at' h:mm a"
): string {
  try {
    const date = parseISO(utcDate);
    return formatInTimeZone(date, timezone, formatStr);
  } catch (error) {
    console.error("Error formatting event time:", error);
    // Fallback to basic formatting if timezone conversion fails
    return format(parseISO(utcDate), formatStr);
  }
}

/**
 * Extract local date and time parts from a UTC timestamp in a specific timezone.
 * Used for populating edit forms with the correct local time values.
 * 
 * @param utcDate - UTC timestamp string from database
 * @param timezone - IANA timezone string
 * @returns Object with date (YYYY-MM-DD) and time (HH:mm) strings in the event's timezone
 * 
 * Example: utcToLocalParts("2026-01-28T00:00:00Z", "America/Jamaica")
 *          → { date: "2026-01-27", time: "19:00" }
 */
export function utcToLocalParts(
  utcDate: string,
  timezone: string
): { date: string; time: string } {
  try {
    const parsed = parseISO(utcDate);
    // Convert UTC to the event's timezone
    const zonedDate = toZonedTime(parsed, timezone);
    
    // Format the zoned date to extract date and time parts
    const dateStr = format(zonedDate, "yyyy-MM-dd");
    const timeStr = format(zonedDate, "HH:mm");
    
    return { date: dateStr, time: timeStr };
  } catch (error) {
    console.error("Error converting UTC to local parts:", error);
    // Fallback: extract directly from ISO string (assumes UTC)
    return {
      date: utcDate.substring(0, 10),
      time: utcDate.substring(11, 16),
    };
  }
}

/**
 * Get a human-readable timezone label
 */
export function getTimezoneLabel(timezone: string): string {
  const labels: Record<string, string> = {
    "America/Jamaica": "Jamaica",
    "America/New_York": "ET",
    "America/Chicago": "CT",
    "America/Denver": "MT",
    "America/Los_Angeles": "PT",
    "America/Phoenix": "AZ",
    "America/Anchorage": "AK",
    "Pacific/Honolulu": "HI",
    "UTC": "UTC",
  };
  return labels[timezone] || timezone.split("/").pop()?.replace("_", " ") || timezone;
}
